import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { srOnly } from "@labxchange/design_tokens";

/* ---------------------------- Internationalization ---------------------------- */

export const messages = defineMessages({
  actionButtonChanged: {
    id: "ActionAnnouncer.actionButtonChanged",
    defaultMessage: "Button changed to {label}.",
    description:
      "Screen-reader announcement made when a question's action button changes label/function (e.g. Submit becomes Try again or Reset).",
  },
});

interface ActionAnnouncerProps {
  /** The current, already-formatted label of the primary action button. */
  label: string;
  /** Optional test id for the live region. */
  testId?: string;
}

/**
 * A visually-hidden polite live region that announces when a question's primary
 * action button changes label/function (Submit -> Try again / Reset), which is
 * otherwise silent to screen readers because the old button unmounts and a new
 * one mounts (WCAG 4.1.3 Status Messages).
 *
 * It announces on every change to `label` after the initial mount. Clearing the
 * region first and setting the message on a later tick forces a DOM change so the
 * region re-announces even when the new label matches a value it held before; a
 * later clear keeps the region empty so it is not re-read during page browsing.
 */
export const ActionAnnouncer: React.FC<ActionAnnouncerProps> = ({
  label,
  testId,
}) => {
  const { formatMessage } = useIntl();
  const [announcement, setAnnouncement] = useState("");
  const prevLabelRef = useRef(label);
  const timersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  // Hold the latest formatMessage in a ref so the announce effect can depend on
  // `label` alone. Depending on `formatMessage` (whose identity changes on every
  // intl re-render) would cancel the pending announce timer whenever the host
  // re-renders for an unrelated reason, and the effect would then early-return
  // (label unchanged) without rescheduling — silently dropping the announcement.
  const formatMessageRef = useRef(formatMessage);
  formatMessageRef.current = formatMessage;

  useEffect(() => {
    // Skip the initial label — no announcement for the initial button state.
    if (prevLabelRef.current === label) return;
    prevLabelRef.current = label;

    const message = formatMessageRef.current(messages.actionButtonChanged, {
      label,
    });
    timersRef.current.forEach(clearTimeout);
    setAnnouncement("");
    const setTimer = setTimeout(() => setAnnouncement(message), 60);
    const clearTimer = setTimeout(() => setAnnouncement(""), 5000);
    timersRef.current = [setTimer, clearTimer];
    return () => {
      clearTimeout(setTimer);
      clearTimeout(clearTimer);
    };
  }, [label]);

  // Clear any pending timers on unmount.
  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  return (
    <div
      className={srOnly}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-lx-action-announcer="true"
      data-testid={testId}
    >
      {announcement}
    </div>
  );
};

export default ActionAnnouncer;
